import { useCallback, useEffect, useRef, useState } from "react";
import {
  addSpeakerNote,
  updateSpeakerNote,
  type SurfDeck,
  type SurfSpeakerNoteKind,
} from "@vida/surf-core";
import {
  askSpeakerWhale,
  applyWhaleAnswerToScript,
  isSpeakerWhaleConfigured,
  materializeWhaleNote,
  type WhaleScriptApplyMode,
} from "../../services/speakerWhale";
import { buildSpeakerNoteFromWhaleAnswer } from "../../services/speakerNoteSummary";
import { formatSpeakerWhaleLiveStep, type SpeakerWhaleMissionStep } from "../../services/speakerWhaleStep";
import { createSurfApiConfig, ensureSurfClientConfig, refreshSurfOceanClientConfig } from "../../services/surfClientConfig";
import {
  getSpeakerWhaleConfigMissing,
  speakerWhaleConfigMissingLabel,
} from "../../services/surfOceanConfig";
import { SpeakerAgentAvatar } from "./SpeakerAgentAvatar";
import { SpeakerScriptView } from "./SpeakerScriptEditor";
import { SpeakerTypingIndicator } from "./SpeakerTypingIndicator";
import { Menu } from "../../ui/Menu";

const DEFAULT_AGENT = { name: "Whale", emoji: "🐋" };

const QUICK_PROMPTS: Array<{ label: string; kind: SurfSpeakerNoteKind; question: string }> = [
  { label: "Approfondisci", kind: "deepdive", question: "Approfondisci il tema di questa slide per il discorso." },
  { label: "Dati & numeri", kind: "data", question: "Quali dati, statistiche o numeri posso citare per questa slide?" },
  { label: "Obiezioni", kind: "objection", question: "Quali obiezioni potrebbero sollevare e come rispondere?" },
  { label: "Aneddoto", kind: "anecdote", question: "Suggerisci un aneddoto o esempio concreto per rendere il punto memorabile." },
  { label: "Q&A", kind: "qa", question: "Quali domande potrebbero fare dal pubblico e come rispondere?" },
  { label: "Definizione", kind: "definition", question: "Definisci in modo chiaro i concetti chiave di questa slide." },
];

const SCRIPT_QUICK_PROMPTS: Array<{ label: string; question: string }> = [
  {
    label: "Riformula discorso",
    question:
      "Riformula il discorso di questa slide rendendolo più scorrevole, mantenendo i contenuti.",
  },
  {
    label: "Aggiungi sezione",
    question: "Proponi una sezione aggiuntiva di discorso per questa slide su: ",
  },
];

type Turn = {
  question: string;
  answer: string;
  agentName: string;
  jobId?: string;
  kind: SurfSpeakerNoteKind;
};

type Props = {
  deck: SurfDeck;
  slideId: string;
  slideLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeckChange: (deck: SurfDeck) => void;
  onNoteAdded?: () => void;
  onActivity?: (label: string | null) => void;
};

function TurnBlock({
  turn,
  onAddNote,
  onApplyToScript,
  added,
}: {
  turn: Turn;
  onAddNote: () => void;
  onApplyToScript: (mode: WhaleScriptApplyMode) => void;
  added: boolean;
}) {
  const scriptMenuItems = [
    { id: "replace", label: "Sostituisci", onClick: () => onApplyToScript("replace") },
    { id: "append", label: "Aggiungi in coda", onClick: () => onApplyToScript("append") },
    { id: "prepend", label: "Aggiungi in testa", onClick: () => onApplyToScript("prepend") },
  ];

  return (
    <div className="surf-speaker-whale-turn">
      <div className="surf-speaker-whale-message surf-speaker-whale-message--user">
        <span className="surf-speaker-whale-message-label">Tu</span>
        <p>{turn.question}</p>
      </div>
      <div className="surf-speaker-whale-message surf-speaker-whale-message--assistant">
        <span className="surf-speaker-whale-message-label surf-speaker-whale-message-label--agent">
          <SpeakerAgentAvatar name={turn.agentName} size={18} />
          {turn.agentName}
        </span>
        <div className="surf-speaker-whale-answer">
          <SpeakerScriptView markdown={turn.answer} />
        </div>
        <div className="surf-speaker-whale-turn-actions">
          <button
            type="button"
            className="surf-btn surf-btn--ghost surf-speaker-whale-add-note"
            onClick={onAddNote}
            disabled={added}
          >
            {added ? "✓ Aggiunta come nota" : "Aggiungi come nota"}
          </button>
          <Menu
            align="start"
            label="Usa nel discorso"
            items={scriptMenuItems}
          />
        </div>
      </div>
    </div>
  );
}

export function SpeakerWhalePanel({
  deck,
  slideId,
  slideLabel,
  open,
  onOpenChange,
  onDeckChange,
  onNoteAdded,
  onActivity,
}: Props) {
  const [draft, setDraft] = useState("");
  const [kind, setKind] = useState<SurfSpeakerNoteKind>("freeform");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [missionSteps, setMissionSteps] = useState<SpeakerWhaleMissionStep[]>([]);
  const [activeAgent, setActiveAgent] = useState(DEFAULT_AGENT);
  const [addedTurnKeys, setAddedTurnKeys] = useState<Set<string>>(() => new Set());
  const [configRevision, setConfigRevision] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const whaleReady = isSpeakerWhaleConfigured();
  const missingConfig = getSpeakerWhaleConfigMissing();
  void configRevision;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void refreshSurfOceanClientConfig().then(() => {
      if (!cancelled) setConfigRevision((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [open, slideId]);

  useEffect(() => {
    if (!open) return;
    setDraft("");
    setError(null);
    setPendingQuestion(null);
    setMissionSteps([]);
    setActiveAgent(DEFAULT_AGENT);
    setTurns([]);
    setAddedTurnKeys(new Set());
    setKind("freeform");
  }, [open, slideId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, busy, pendingQuestion, missionSteps]);

  const liveStepLabel = formatSpeakerWhaleLiveStep(missionSteps, activeAgent.name);

  const turnKey = (turn: Turn) => `${turn.question}::${turn.jobId ?? turn.answer.slice(0, 32)}`;

  const handleApplyToScript = useCallback(
    (turn: Turn, mode: WhaleScriptApplyMode) => {
      onDeckChange(applyWhaleAnswerToScript(deck, slideId, turn.answer, mode));
      onActivity?.("Discorso aggiornato");
      setTimeout(() => onActivity?.(null), 2000);
    },
    [deck, onActivity, onDeckChange, slideId],
  );

  const handleAddNote = useCallback(
    (turn: Turn) => {
      const key = turnKey(turn);
      if (addedTurnKeys.has(key)) return;

      const fallbackNote = materializeWhaleNote(
        turn.question,
        { answer: turn.answer, jobId: turn.jobId, agentName: turn.agentName },
        turn.kind,
      );
      let nextDeck = addSpeakerNote(deck, slideId, fallbackNote);
      onDeckChange(nextDeck);
      setAddedTurnKeys((prev) => new Set(prev).add(key));
      onNoteAdded?.();
      onActivity?.("Sunto…");

      void (async () => {
        try {
          await ensureSurfClientConfig();
          const coherent = await buildSpeakerNoteFromWhaleAnswer(
            createSurfApiConfig(),
            turn.question,
            turn.answer,
            turn.agentName,
            turn.jobId,
            turn.kind,
          );
          if (coherent.summaryStatus !== "coherent") return;
          nextDeck = updateSpeakerNote(nextDeck, slideId, fallbackNote.id, {
            body: coherent.body,
            rawBody: coherent.rawBody,
            question: coherent.question,
            summaryStatus: coherent.summaryStatus,
          });
          onDeckChange(nextDeck);
          onActivity?.("Sunto coerente");
        } catch {
          /* keep fallback */
        } finally {
          setTimeout(() => onActivity?.(null), 2000);
        }
      })();
    },
    [addedTurnKeys, deck, onActivity, onDeckChange, onNoteAdded, slideId],
  );

  const submitQuestion = useCallback(async () => {
    const question = draft.trim();
    if (!question || busy) return;
    if (!whaleReady) {
      const missing = missingConfig.map(speakerWhaleConfigMissingLabel).join(", ");
      setError(
        missing
          ? `Config Whale incompleta: ${missing}.`
          : "Whale non configurato per questo ambiente.",
      );
      return;
    }

    const activeKind = kind;
    setDraft("");
    setError(null);
    setPendingQuestion(question);
    setMissionSteps([]);
    setBusy(true);
    setActiveAgent(DEFAULT_AGENT);

    try {
      const result = await askSpeakerWhale(
        deck,
        slideId,
        question,
        (p) => {
          if (p.agentName) {
            setActiveAgent({ name: p.agentName, emoji: DEFAULT_AGENT.emoji });
          }
          if (p.kind === "step" && p.step) {
            setMissionSteps((prev) => {
              if (prev.some((s) => s.seq === p.step!.seq)) return prev;
              return [...prev, p.step!];
            });
          }
        },
        {
          priorTurns: turns.map((t) => ({
            question: t.question,
            answer: t.answer,
            agentName: t.agentName,
          })),
        },
      );

      const turn: Turn = {
        question,
        answer: result.answer,
        agentName: result.agentName,
        jobId: result.jobId,
        kind: activeKind,
      };
      setTurns((prev) => [...prev, turn]);
      setPendingQuestion(null);
      setMissionSteps([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore Whale.");
      setDraft(question);
    } finally {
      setBusy(false);
    }
  }, [busy, deck, draft, kind, missingConfig, slideId, turns, whaleReady]);

  if (!open) return null;

  return (
    <div className="surf-speaker-whale-popover" role="dialog" aria-label={`Whale — ${slideLabel}`}>
      <header className="surf-speaker-whale-popover-header">
        <SpeakerAgentAvatar
          name={DEFAULT_AGENT.name}
          emoji={DEFAULT_AGENT.emoji}
          size={24}
          className="surf-speaker-whale-popover-agent-icon"
        />
        <h3 className="surf-speaker-whale-popover-heading" title={slideLabel}>
          {slideLabel}
        </h3>
        <div className="surf-speaker-whale-popover-spacer" aria-hidden />
        <button
          type="button"
          className="surf-speaker-whale-header-icon-btn"
          onClick={() => onOpenChange(false)}
          aria-label="Chiudi"
        >
          ✕
        </button>
      </header>

      <div className="surf-speaker-whale-message-list" ref={listRef}>
        {!whaleReady && (
          <div className="surf-speaker-whale-config-hint">
            <p className="surf-speaker-whale-error">Whale non è configurato per questo ambiente.</p>
            {missingConfig.length > 0 && (
              <ul className="surf-speaker-whale-config-missing">
                {missingConfig.map((field) => (
                  <li key={field}>{speakerWhaleConfigMissingLabel(field)}</li>
                ))}
              </ul>
            )}
            <p className="surf-speaker-whale-config-help">
              Imposta <code>surf_whale_agent_id</code> (ORCHESTRATOR con app <strong>surf</strong> in
              Currents) oppure usa il fallback <code>surf_researcher_agent_id</code>. In locale:{" "}
              <code>bash cloud/scripts/surf-web-sync-env.sh</code>
            </p>
          </div>
        )}
        {turns.length === 0 && !busy && !pendingQuestion && whaleReady && (
          <p className="surf-speaker-whale-empty">
            Chiedi approfondimenti per questa slide — la risposta resta nel thread.
          </p>
        )}
        {turns.map((turn, index) => (
          <TurnBlock
            key={`${index}-${turn.question.slice(0, 12)}`}
            turn={turn}
            onAddNote={() => handleAddNote(turn)}
            onApplyToScript={(mode) => handleApplyToScript(turn, mode)}
            added={addedTurnKeys.has(turnKey(turn))}
          />
        ))}
        {pendingQuestion && (
          <div className="surf-speaker-whale-turn">
            <div className="surf-speaker-whale-message surf-speaker-whale-message--user">
              <span className="surf-speaker-whale-message-label">Tu</span>
              <p>{pendingQuestion}</p>
            </div>
            {busy && (
              <div className="surf-speaker-whale-message surf-speaker-whale-message--assistant">
                <span className="surf-speaker-whale-message-label surf-speaker-whale-message-label--agent">
                  <SpeakerAgentAvatar name={activeAgent.name} size={18} />
                  {activeAgent.name}
                </span>
                {missionSteps.length > 0 ? (
                  <p className="surf-speaker-whale-step" title={liveStepLabel}>
                    {liveStepLabel}
                  </p>
                ) : (
                  <SpeakerTypingIndicator />
                )}
              </div>
            )}
          </div>
        )}
        {error && <p className="surf-speaker-whale-error">{error}</p>}
      </div>

      <footer className="surf-speaker-whale-composer">
        <div className="surf-speaker-whale-chips" role="group" aria-label="Prompt rapidi">
          {QUICK_PROMPTS.map((chip) => (
            <button
              key={chip.kind}
              type="button"
              className={`surf-speaker-whale-chip${kind === chip.kind ? " is-active" : ""}`}
              disabled={busy}
              onClick={() => {
                setKind(chip.kind);
                setDraft(chip.question);
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div className="surf-speaker-whale-chips surf-speaker-whale-chips--script" role="group" aria-label="Prompt discorso">
          {SCRIPT_QUICK_PROMPTS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              className="surf-speaker-whale-chip surf-speaker-whale-chip--script"
              disabled={busy}
              onClick={() => {
                setKind("freeform");
                setDraft(chip.question);
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <textarea
          rows={2}
          value={draft}
          placeholder="Chiedi un approfondimento per questa slide…"
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submitQuestion();
            }
          }}
        />
        <button
          type="button"
          disabled={busy || !draft.trim() || !whaleReady}
          onClick={() => void submitQuestion()}
        >
          Invia
        </button>
      </footer>
    </div>
  );
}
